import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ProfileView from '../components/profile/ProfileView';
import EnhancedProfileEditor from '../components/profile/EnhancedProfileEditor';
import socialService, { ProfileDto } from '../services/socialService';
import { useAuth } from '../hooks/useAuth';
import {
  isProfileCacheStale,
  readCachedProfile,
  writeCachedProfile,
} from '../services/profileLocalStore';

const PublicProfile: React.FC = () => {
  const { aliasName } = useParams<{ aliasName: string }>();
  const { user } = useAuth();
  const viewerUsername = (user?.username ?? '').trim();
  const [profile, setProfile] = useState<ProfileDto | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewContentTab, setViewContentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cacheStale, setCacheStale] = useState(false);

  const loadProfile = useCallback(async (forceNetwork = false) => {
    if (!aliasName) return;

    const cached = viewerUsername ? readCachedProfile(viewerUsername, aliasName) : null;
    if (cached && !forceNetwork && !isProfileCacheStale(cached.cachedAt)) {
      setProfile(cached.profile);
      setCanEdit(cached.canEdit);
      setCacheStale(false);
      setError(null);
      setLoading(false);
      return;
    }

    if (cached && !forceNetwork) {
      setProfile(cached.profile);
      setCanEdit(cached.canEdit);
      setCacheStale(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const profileResult = await socialService.getProfile(aliasName, {
        ifNoneMatch: !forceNetwork ? cached?.etag : undefined,
      });

      if (profileResult.notModified && cached) {
        setProfile(cached.profile);
        setCanEdit(cached.canEdit);
        setCacheStale(isProfileCacheStale(cached.cachedAt));
        return;
      }

      if (!profileResult.data) {
        throw new Error('Profile not found or not accessible');
      }

      setProfile(profileResult.data.profile);
      setCanEdit(Boolean(profileResult.data.canEdit));
      setCacheStale(false);
      if (viewerUsername) {
        writeCachedProfile(
          viewerUsername,
          aliasName,
          profileResult.data.profile,
          Boolean(profileResult.data.canEdit),
          profileResult.etag,
        );
      }
    } catch (err: unknown) {
      console.error('Failed to load profile:', err);
      if (cached) {
        setProfile(cached.profile);
        setCanEdit(cached.canEdit);
        setCacheStale(true);
        setError('Could not refresh profile. Showing cached copy.');
      } else {
        setError('Profile not found or not accessible');
      }
    } finally {
      setLoading(false);
    }
  }, [aliasName, viewerUsername]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  if (loading && !profile) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading profile...</Typography>
      </Container>
    );
  }

  if (error && !profile) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Profile not found'}
        </Alert>
      </Container>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {cacheStale && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={() => void loadProfile(true)}>
              Refresh
            </Button>
          }
        >
          This profile may be out of date (cached over 24 hours ago).
        </Alert>
      )}
      {isEditing && canEdit ? (
        <EnhancedProfileEditor
          profile={profile}
          onSave={async () => {
            await loadProfile(true);
            setIsEditing(false);
          }}
          onCancel={() => setIsEditing(false)}
          onProfileRefresh={() => loadProfile(true)}
          initialTab={viewContentTab}
          onTabChange={setViewContentTab}
        />
      ) : (
        <ProfileView
          profile={profile}
          isOwner={canEdit}
          onEdit={canEdit ? () => setIsEditing(true) : undefined}
          contentTabValue={viewContentTab}
          onContentTabChange={setViewContentTab}
        />
      )}
    </Container>
  );
};

export default PublicProfile;
